import React, { useEffect, useMemo, useState } from 'react';
import { Contact, ExternalLink, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import axiosInstance from '../utils/axiosInstance';
import V2Shell from './V2Shell';

// Actions: assigned tickets where the customer has waited over a week, rated by
// Replai so the team knows what to pick up first (Geoff, 2026-10-19).
const API = 'api/replai/v2/actions/';
const GORGIAS = 'https://lamptwist.gorgias.com/app/ticket/';
const URG = {
  critical: 'bg-red-100 text-red-800', high: 'bg-amber-100 text-amber-800',
  normal: 'bg-[var(--line-2)] text-[var(--ink-2)]', low: 'bg-[var(--paper)] text-[var(--ink-3)]', '': 'bg-[var(--paper)] text-[var(--ink-3)]',
};
const MOOD = { angry: '😠 angry', frustrated: '😕 frustrated' };

const ActionsV2 = () => {
  const [d, setD] = useState(null);
  const [all, setAll] = useState(false);
  const [who, setWho] = useState('');
  const [busy, setBusy] = useState(false);
  const load = () => axiosInstance.get(API).then(({ data }) => setD(data)).catch(() => setD(false));
  useEffect(() => { load(); }, []);
  const items = useMemo(() => (d?.items || []).filter((x) => (all || x.team) && (!who || x.assignee === who)), [d, all, who]);
  const people = useMemo(() => {
    const c = {};
    (d?.items || []).filter((x) => all || x.team).forEach((x) => { c[x.assignee] = (c[x.assignee] || 0) + 1; });
    return Object.entries(c).sort((a, b) => b[1] - a[1]);
  }, [d, all]);
  const refresh = () => { setBusy(true); axiosInstance.post(API).finally(() => setTimeout(() => { load(); setBusy(false); }, 4000)); };
  return (
    <V2Shell>
      <div className="px-6 py-10 sm:px-10 xl:px-14">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-[2.4rem] font-semibold leading-[1.1]">Actions</h1>
            <p className="prose-body mt-2 max-w-3xl text-[var(--ink-2)]">
              Open tickets assigned to someone where the customer has waited more than a week for a reply. Replai read
              each thread and says how urgent it is, how the customer feels, and what to do next.
            </p>
          </div>
          <button type="button" onClick={refresh} disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-md border border-[var(--line)] bg-[var(--surface)] px-3 py-1.5 text-sm disabled:opacity-50">
            <RefreshCw size={14} className={busy ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
        <div className="mt-5 flex flex-wrap items-center gap-1.5">
          <button type="button" onClick={() => setWho('')} className={`rounded-full px-3 py-1 text-sm ${!who ? 'bg-[var(--ink)] text-white' : 'bg-[var(--line-2)] text-[var(--ink-2)]'}`}>Everyone</button>
          {people.map(([p, n]) => (
            <button key={p} type="button" onClick={() => setWho(who === p ? '' : p)}
              className={`rounded-full px-3 py-1 text-sm ${who === p ? 'bg-[var(--ink)] text-white' : 'bg-[var(--line-2)] text-[var(--ink-2)]'}`}>{p} ({n})</button>))}
          <label className="ml-auto flex items-center gap-2 text-sm text-[var(--ink-2)]">
            <input type="checkbox" checked={all} onChange={(e) => { setAll(e.target.checked); setWho(''); }} /> Show other assignees too
          </label>
        </div>
        {d === null && <div className="mt-6 h-64 animate-pulse rounded-lg bg-[var(--line-2)]" />}
        {d === false && <p className="mt-6 text-sm text-red-700">Could not load the actions.</p>}
        {d && !items.length && <p className="mt-6 rounded-lg border border-dashed border-[var(--line)] p-6 text-sm text-[var(--ink-3)]">Nobody is waiting more than a week. Nice.</p>}
        {d && items.length > 0 && (
          <ul className="mt-5 space-y-2">
            {items.map((x) => (
              <li key={x.ticket} className="rounded-lg border border-[var(--line)] bg-[var(--surface)] p-3.5">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${URG[x.urgency || '']}`}>{x.urgency || 'not rated yet'}</span>
                  {MOOD[x.mood] && <span className="text-xs">{MOOD[x.mood]}</span>}
                  <span className="font-medium">{x.subject || '(no subject)'}</span>
                  <span className="text-[var(--ink-3)]">· {x.customer}</span>
                  <span className="ml-auto text-xs text-[var(--ink-3)]"><b className="num text-[var(--ink-2)]">{x.waiting_days}</b> days waiting · {x.assignee}</span>
                </div>
                {x.next_step && <p className="mt-1.5 text-sm"><b>Next:</b> {x.next_step}</p>}
                {x.reason && <p className="mt-0.5 text-xs text-[var(--ink-3)]">{x.reason}</p>}
                <div className="mt-2 flex gap-4 text-xs">
                  <a href={`${GORGIAS}${x.ticket}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[var(--ink-2)] hover:underline">Open in Gorgias <ExternalLink size={11} /></a>
                  {x.email && <Link to={`/customers?email=${encodeURIComponent(x.email)}`} className="inline-flex items-center gap-1 text-[var(--ink-2)] hover:underline"><Contact size={12} /> Customer timeline</Link>}
                </div>
              </li>))}
          </ul>
        )}
      </div>
    </V2Shell>
  );
};

export default ActionsV2;
